import { forwardRef, memo, MutableRefObject, useCallback, useContext, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SplitButton from '../../../components/SplitButton'
import Table from '../../../components/Table'

import './index.scss'
import { getLiveMapViewInfo } from '../data'
import useDynamicRef from '../../../hooks/useDynamicRef'
import { DTO_MobileLocation } from '../../../services/calls/types/positions'
import StopSpeedCol from './table/StopSpeedCol'
import LastPosCol from './table/LastPosCol'
import { FlotiMapRef } from '../../../components/FlotiMap/type'
import { LAYER_TYPE } from '../../../components/FlotiMap'
import MobileDetailCol from './table/MobileDetailCol'
import UserContext from '../../../contexts/UserContext/context'
import ButtonSelect from '../../../components/Input/ButtonSelect'
import Icon from '../../../components/Icon'
import { ICON } from '../../../components/Icon/data'

type SidebarLiveMap = {
  mapRef: MutableRefObject<FlotiMapRef>
  onFleetChange: (fleetid: number) => void
  defaultFleet: number
}

const LiveMap_Sidebar = memo(forwardRef(({
  mapRef, onFleetChange, defaultFleet
}: SidebarLiveMap, ref) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [view, setView] = useState<string>('VEHICLES')
  const [tableData, setTableData] = useState<DTO_MobileLocation[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fleetDisplay, setFleetDisplay] = useState<number | null>(defaultFleet)

  const availableViews = useMemo(() => ({
    VEHICLES: t('Vehicles view'),
    DRIVERS: t('Drivers view')
  }), [t])

  const columnInfo = useMemo(() => {
    const columns = getLiveMapViewInfo(view)
    return columns
  }, [view])

  const fleetOptions = useMemo(() => {
    const options: Record<number, string> = {}
    user.available_fleets.forEach((fleet) => {
      options[fleet.fleetid] = fleet.name
    })
    return options
  }, [user.available_fleets, fleetDisplay])

  const mappedHeaderCols = useMemo(() => {
    const lookup = {}
    if (columnInfo?.length) {
      columnInfo.forEach((col, index) => {
        const { id } = col
        lookup[id] = {
          ...col,
          index,
          formatFunction: (val, row) => {
            if (id === 'STOPPED_SPEED') {
              return <StopSpeedCol val={val} data={row} />
            } else if (id === 'LAST_POSITION') {
              return <LastPosCol val={val} />
            } else if (id === 'DETAILS') {
              return <MobileDetailCol mobileid={val} />
            }
            return val
          }
        }
      })
    }
    return lookup
  }, [columnInfo])

  const rows = useMemo(() => {
    const rowArr = []
    if (tableData?.length) {
      tableData.map((mobile) => {
        const newRow = { ...mobile }
        Object.keys(mappedHeaderCols).map((id) => {
          const { path } = mappedHeaderCols[id]
          const value = mobile[path]
          newRow[id] = value ?? ''
        })
        rowArr.push(newRow)
      })
    }
    return rowArr
  }, [tableData, mappedHeaderCols])

  const setData = (data: DTO_MobileLocation[]) => {
    setTableData(data)
  }

  const forwardRefAction = useDynamicRef({
    setData,
    setIsLoading,
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  const zoomToMobile = useCallback((row: Record<string, any>) => {
    const { lat, lon } = row
    mapRef.current.setMapSettings({ center: [lat, lon], zoom: 15 }, true)
    mapRef.current.makeBlink(row.mobileid, LAYER_TYPE.MARKERS)
  }, [mapRef.current])

  const fleetSelectElem = useMemo(() => {
    if (user.available_fleets.length > 1) {
      return (
        <ButtonSelect
          size="l"
          label={<>
            <div className="LiveMap__button-label">
              {fleetDisplay ? fleetOptions[fleetDisplay] : t('Change fleet')}
            </div>
            <Icon icon={ICON.CARS} size={16} />
          </>}
          options={Object.keys(fleetOptions).reduce((obj, key) => {
            if (key !== fleetDisplay?.toString()) {
              const fleet = fleetOptions[key]
              obj[key] = fleet
            }
            return obj
          }, {} as Record<number, string>)}
          onChange={(val) => {
            onFleetChange(val)
            setFleetDisplay(Number(val))
          }}
        />
      )
    }

    return null
  }, [fleetOptions, user.available_fleets, t, fleetDisplay])

  return (
    <div className="LiveMap__sidebar">
      <SplitButton
        options={availableViews}
        onClick={(val) => setView(val)}
        value={view}
        size="s"
      />
      <Table
        isLoading={isLoading}
        showSearch={true}
        tableHeader={mappedHeaderCols}
        rows={rows}
        includePageFooter={true}
        onRowClick={(row) => zoomToMobile(row)}
        headerElems={[fleetSelectElem]}
        defaultSort={{ column: 'CAR_NAME', type: 'ascending' }}
      />
    </div>
  )
}))

export default LiveMap_Sidebar
